import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

// A text the bot shows on screen, quoted exactly.
function Msg({ children, red }) {
  return (
    <span className={`font-mono text-xs ${red ? 'text-red-400' : 'text-gray-300'}`}>{children}</span>
  );
}

function PageAutoMelt({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">
        Melt &amp; Cubes
      </h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Melting turns the contents of your bag into dust and materials without a merchant: runes
        into rune dust, gear into runes of power, gems into shiny dust. The same place also opens
        the Mystic cubes you collect. It has four pages under FARM, one for each kind:{' '}
        <strong className="text-white">Melt runes</strong>, <strong className="text-white">Melt gems</strong>,{' '}
        <strong className="text-white">Melt items</strong> and <strong className="text-white">Open cubes</strong>.
      </p>

      <Callout type="danger">
        <strong>Melting cannot be undone.</strong> Same discipline as selling: Dry run is on by
        default, the defaults are narrow, and locked slots are never touched.
      </Callout>

      <p className="text-sm text-gray-400 mb-4">
        Every page has the same two halves. On the left, the card of that kind: what is in your
        bag, its own settings, its own <strong className="text-white">&hellip; automatically</strong>{' '}
        switch, its own key and its button. On the right, the part all four share:{' '}
        <strong className="text-white">MELT</strong> (<strong>WHAT WOULD GO</strong> and{' '}
        <strong>ADVANCED &rsaquo; Dry run</strong>) and <strong className="text-white">STATUS</strong>.
      </p>

      <DocHeading>The Safe Way to Start</DocHeading>
      <Step n={1} title="Leave Dry run on">
        <strong>Dry run</strong> sits under <strong>ADVANCED</strong> on the <strong>MELT</strong>{' '}
        card. While it is on, the buttons read <strong>PREVIEW RUNES</strong>,{' '}
        <strong>PREVIEW GEMS</strong>, <strong>PREVIEW ITEMS</strong> and{' '}
        <strong>PREVIEW CUBES</strong>, and nothing is melted. With ADVANCED closed, an orange line
        says <em>Dry run is on (Advanced) - nothing is actually melted.</em>
      </Step>
      <Step n={2} title="Lock what must never go">
        On{' '}
        <button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">FARM &rsaquo; Bag</button>,
        lock the slots that hold runes, gems and gear you want to keep.
      </Step>
      <Step n={3} title="Set each page">
        Choose the gem grades and how many of each gem to keep, and the gear rarities.
      </Step>
      <Step n={4} title="Press WHAT WOULD GO">
        A list window shows every unlocked slot, which of the four would take it, and why.
      </Step>
      <Step n={5} title="Only then turn Dry run off">
        The buttons change to <strong>MELT RUNES</strong>, <strong>MELT GEMS</strong>,{' '}
        <strong>MELT ITEMS</strong> and <strong>OPEN CUBES</strong>.
      </Step>

      <Shot
        name="auto-melt-01-runes.png"
        tab="FARM › Melt runes"
        caption="Melt runes after a pass: 24 runes in the bag, 142 melted so far, melting by itself after pickups."
        shoot="menushot (stage melt + meltgame): Dry run KI, a Melt runes lap — RUNES kártya (24 a táskában, 142 olvasztva, Melt runes automatically BE, Rune key F9), jobbra MELT + STATUS (Done, 12 / 12)."
      />

      <DocHeading>The Four Pages</DocHeading>
      <DocTable
        headers={['Page', 'Its button takes', 'You get', 'Its own settings']}
        rows={[
          [<strong className="text-white">Melt runes</strong>, 'Every rune in an unlocked slot, whatever its grade.', 'Rune dust', 'None - runes have no filter.'],
          [<strong className="text-white">Melt gems</strong>, 'Gems of the grades you chose, above the amount you keep.', 'Shiny dust', 'Gem grade, Keep per gem type. The card lists each gem type with its stock and how many would go.'],
          [<strong className="text-white">Melt items</strong>, 'Gear of the rarities you chose - unidentified, and identified too if you allowed it.', 'Runes of power', 'Unidentified gear, Melt identified gear too, Identified gear.'],
          [<strong className="text-white">Open cubes</strong>, 'The Mystic cubes in your bag.', 'Whatever is inside', 'Per burst, Delay.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        Each page shows what it would work on (<strong className="text-white">In your bag</strong>,
        from unlocked slots only) and a session counter (<strong className="text-white">Melted so far</strong>{' '}
        or <strong className="text-white">Opened so far</strong>). Each has its own key right above
        its button: a key starts its operation (a preview while Dry run is on) and, pressed again,
        stops it. Keys only react while the game window is in front. A key that is easy to hit by
        accident (Caps Lock, Shift, Ctrl, Alt, Space, Enter and similar) gets a red warning. While an
        operation runs, a <strong className="text-white">STOP</strong> button appears on the MELT card.
      </p>
      <Callout type="warning">
        <strong>Runes have no filter.</strong> MELT RUNES takes every rune in every unlocked slot, of
        every grade &mdash; the card says so itself:{' '}
        <Msg>Runes have no filter: melting them does not sort by grade.</Msg> Lock the slots of the
        runes you want to keep.
      </Callout>

      <Shot
        name="auto-melt-02-gems.png"
        tab="FARM › Melt gems"
        caption="Melt gems: Polished grade, keep 10 of each type - 20 gems would go."
        shoot="menushot (stage melt): Melt gems lap — 57 kő 4 fajtában, Gem grade „e Polished”, Keep per gem type 10, a fajták listája (Amethyst 21 -> 11 ...), Melt gems automatically BE, Gem key F10."
      />

      <DocHeading>Melting by Itself</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Every page has its own switch: <strong className="text-white">Melt runes automatically</strong>,{' '}
        <strong className="text-white">Melt gems automatically</strong>,{' '}
        <strong className="text-white">Melt items automatically</strong> and{' '}
        <strong className="text-white">Open cubes automatically</strong>. A green dot next to the
        page in the list on the left shows which ones are on.
      </p>
      <DocTable
        headers={['Switch', 'What it does']}
        rows={[
          [<strong className="text-white">&hellip; automatically</strong>, 'Shortly after the bot picks something up, it runs that pass by itself, with the same filters as the button. It never interrupts a pass that is already running. When you switch it on, it does one round about a second later, without waiting for a pickup.'],
          [<strong className="text-white">Open cubes automatically</strong>, 'Opens your Mystic cubes shortly after a pickup. It waits while your bag is full, because the reward needs a free slot. Opening a cube is not melting: it costs nothing and cannot destroy anything you own. Only Mystic cubes are opened - event chests and premium packs are never touched.'],
          [<><strong className="text-white">Per burst</strong> / <strong className="text-white">Delay</strong></>, 'On the Open cubes page: how many cubes it opens at once (default 5), and the pause between bursts (default 100 ms). The defaults are fine; slow down if the game starts to stutter.'],
        ]}
      />
      <Callout type="warning">
        <strong>Everything here obeys Dry run.</strong> With Dry run on it melts nothing by itself
        either, and the card says{' '}
        <Msg>Dry run is on - it will not melt anything on its own either.</Msg> &ldquo;After a
        pickup&rdquo; means pickups made by the bot (<button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Loot</button>), not items you pick up by hand.
      </Callout>

      <Shot
        name="auto-melt-03-items.png"
        tab="FARM › Melt items"
        caption="Melt items: unidentified gear from Uncommon to Rare; identified gear stays."
        shoot="menushot (stage melt): Melt items lap — 9 azonosítatlan felszerelés, Unidentified gear: Uncommon, Magic, Rare; Melt identified gear too KI, Melt items automatically BE, Item key F11."
      />
      <Shot
        name="auto-melt-04-cubes.png"
        tab="FARM › Open cubes"
        caption="Open cubes: 6 in the bag, 38 opened so far, opening by itself."
        shoot="menushot (stage melt + useitem 38): Open cubes lap — 6 kocka, 38 nyitva, Open cubes automatically BE, Per burst 5, Delay 100 ms."
      />

      <DocHeading>What to Melt</DocHeading>
      <DocTable
        headers={['Setting', 'What it does']}
        rows={[
          [<strong className="text-white">Unidentified gear</strong>, 'Which rarities MELT ITEMS may take from unidentified gear: Uncommon, Magic, Rare, Epic, Unique, Set, Mythic. Default: Uncommon and Magic. There is no Common entry: unidentified Common gear, and unidentified gear whose rarity the bot cannot read, is always taken by MELT ITEMS — this list cannot hold it back.'],
          [<strong className="text-white">Melt identified gear too</strong>, 'Off by default. Turn it on only if you also want MELT ITEMS to take identified gear — the gear you already know the value of.'],
          [<strong className="text-white">Identified gear</strong>, <>Appears once the switch above is on: its own rarity list, separate from the unidentified one (default Uncommon and Magic). Identified gear with an unreadable rarity is never melted, and neither is identified Common gear, because the bot cannot tell Common from an unreadable rarity &mdash; the card says <Msg>Identified gear with an unreadable rarity is never melted - the rarity IS the value.</Msg></>],
          [<strong className="text-white">Gem grade</strong>, 'Which gem grades MELT GEMS may take, from a (Flawed) to i (Royal). Default: the two lowest.'],
          [<strong className="text-white">Keep per gem type</strong>, 'How many of each gem type to leave (0–50, default 1). It counts all stacks of that gem together: with 999 + 201 of one gem and Keep 1, one gem is left in total, not one per stack.'],
        ]}
      />
      <Callout type="info">
        <strong>Some things are never melted.</strong> Jewels are left alone &mdash; the game treats
        them as their own kind, and they are worth more than dust. Gems above grade i (the Trapezoid,
        Imperial and Square grades) are not taken by MELT GEMS. Gems in locked slots do not count
        toward <em>Keep per gem type</em>, so a locked stack never makes the bot take more from your
        unlocked ones.
      </Callout>

      <DocHeading>The WHAT WOULD GO Window</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">WHAT WOULD GO</strong> button (it becomes{' '}
        <strong className="text-white">HIDE THE LIST</strong>) opens a separate window that stays
        open with the menu closed. It lists every unlocked slot with <strong>MELT</strong> or{' '}
        <strong>keep</strong> and the reason. The top line is green while Dry run is on (<Msg>DRY RUN is ON - this is a preview, nothing is melted.</Msg>) and red when it is off (<Msg red>DRY RUN is OFF - the four buttons WILL take these for real.</Msg>). It works the
        same way as the list on{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>:{' '}
        <strong className="text-white">Refresh now</strong>, <strong className="text-white">Close</strong>{' '}
        and <strong className="text-white">Show keep items too</strong>.
      </p>
      <DocTable
        headers={['Reason', 'What it means']}
        rows={[
          [<Msg>{'rune - MELT RUNES takes it'}</Msg>, 'This rune goes when you press MELT RUNES.'],
          [<Msg>{'unidentified gear - MELT ITEMS takes it'}</Msg>, <>Goes with MELT ITEMS (or <Msg>identified gear - …</Msg>).</>],
          [<Msg>{'gem: stock N, keep K -> M melted'}</Msg>, <>How many of this gem you have, how many stay, and how many MELT GEMS takes. It reads <Msg>{'-> nothing'}</Msg> when the stock is at or below the keep amount.</>],
          [<Msg>{'cube - OPEN CUBES opens N'}</Msg>, 'How many cubes of this stack would be opened.'],
          [<Msg>{"gem: tier 'x' is not selected"}</Msg>, <>That gem grade is not ticked (<Msg>{"is not meltable"}</Msg> means the grade can never be melted).</>],
          [<Msg>{'rarity N is not selected'}</Msg>, 'That gear rarity is not ticked. The number is the rarity: 1 Uncommon, 2 Magic, 3 Rare, 4 Epic, 5 Unique, 6 Set, 7 Mythic.'],
          [<Msg>{'rarity is unreadable - not touched'}</Msg>, 'Identified gear whose rarity cannot be read, or identified Common gear. It is never melted.'],
          [<Msg>{'identified gear is off'}</Msg>, 'Melt identified gear too is switched off.'],
          [<Msg>{'no melt rule matches (category N)'}</Msg>, 'None of the four buttons handles this kind of item.'],
        ]}
      />

      <DocHeading>The STATUS Card</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Status</strong>, <>What it is doing right now, for example <Msg>Reading the bag...</Msg>, <Msg>Melting runes...</Msg>, <Msg>Opening cubes...</Msg>, <Msg>Done</Msg> or <Msg>Dry run</Msg>.</>],
          [<strong className="text-white">In the plan</strong>, <>How many items the current operation covers. <Msg>(more follows)</Msg> means the plan was full: after this round it reads the bag again and continues by itself.</>],
          [<strong className="text-white">Progress</strong>, 'Where it is in that plan, while running.'],
          [<strong className="text-white">Sent</strong>, 'How many rune and gem melt commands went out in the last run. This line (and the two below) only appears after a rune or gem run that had something to report.'],
          [<strong className="text-white">Held back</strong>, 'Runes or gems the last check kept, because something changed after planning (you locked the slot, moved the item, changed a setting).'],
          [<strong className="text-white">Refused</strong>, 'Commands the game did not accept, with the last reason.'],
        ]}
      />

      <DocHeading>When It Does Not Melt</DocHeading>
      <DocTable
        headers={['You see', 'What it means']}
        rows={[
          [<Msg>Dry run</Msg>, 'Dry run is on (under ADVANCED). Nothing is sent.'],
          [<><Msg>No runes in the bag</Msg>, <Msg>No cubes in the bag</Msg></>, 'Nothing of that kind in your unlocked slots.'],
          [<Msg>No gems above the keep amount</Msg>, 'Every selected gem type is at or below Keep per gem type. Check the gem list in IN THE BAG.'],
          [<Msg>Nothing matches the filter</Msg>, 'No gear of the ticked rarities. Open WHAT WOULD GO for the reasons.'],
          [<Msg red>The inventory could not be read by slot - nothing would be melted.</Msg>, <>The bot could not tell which item sits in which slot, so it touches nothing (status <Msg>Stopped: the inventory could not be read by slot</Msg>).</>],
          [<Msg red>Bag data looks wrong - melting is blocked.</Msg>, <>The bag reading does not make sense this moment (status <Msg>Stopped: bag data looks wrong</Msg>). It clears by itself.</>],
          [<Msg>Too many locked slots to guard safely - nothing melted.</Msg>, 'A pop-up: more locked items than one run can keep track of, so it melts nothing rather than protect only some. Unlock slots you no longer need.'],
          [<Msg red>Item list truncated - some items are not visible to the bot.</Msg>, 'You carry more items than the bot can read at once. Run it twice.'],
          [<Msg red>Stopped: the melt command does not look like it used to. Probably a game patch.</Msg>, <>A game update changed something, and the feature locked itself (status <Msg>Disabled: unexpected command layout</Msg>). Wait for a bot update.</>],
        ]}
      />

      <Callout type="info">
        Locked slots are shared with{' '}
        <button onClick={() => navigate('auto-sell')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Sell</button>{' '}
        &mdash; a slot locked on{' '}
        <button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">FARM &rsaquo; Bag</button>{' '}
        is skipped here as well. Your melt settings can be saved in a{' '}
        <button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">farm profile</button>;
        loading one never switches melting on by itself, never touches Dry run and never changes
        your keys. The <em>Open cubes automatically</em> switch, however, is saved with the profile,
        so loading a profile can turn cube opening on.
      </Callout>
    </div>
  );
}

export default PageAutoMelt;
